import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { Form, useFormikContext } from 'formik-2';
import css from './WizardLayout.css';
import { WarningMessage } from '../../../../components/widgets';
import GeneralPopup, { PopupTypes } from '../../../../components/organisms/GeneralPopup';
import { EMPTY_TEST_CHANNEL } from '../consts';

const prefixTranslate = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD';

const WizardLayout = ({
    buttons,
    title,
    children,
    onSaveDraft,
    shouldShowDraft = true,
    isContentScrollable = true,
    submitData = {},
    activeKey,
}) => {
    const { validateForm, setTouched, setFieldValue, values, setFieldError } = useFormikContext();
    const [showPopup, setShowPopup] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isSaving && !submitData.loading) {
            setShowPopup(false);
            setIsSaving(false);
        }
    }, [submitData.loading, isSaving]);

    const onSaveDraftClick = useCallback(async () => {
        const errors = await validateForm();
        if (errors?.testChannels?.length === 1 && errors?.testChannels[0]?.name) {
            await setFieldValue('testChannels', []);
            setShowPopup(true);
            return;
        }
        setTouched(errors, true);
        if (Object.keys(errors).length) return;
        setShowPopup(true);
    }, [validateForm, setFieldValue, setTouched]);

    const handlePopupAccept = useCallback(() => {
        setIsSaving(true);
        onSaveDraft(activeKey);
    }, [onSaveDraft, activeKey]);

    const handlePopupCancel = useCallback(async () => {
        setShowPopup(false);

        if (values?.testChannels && values.testChannels.length === 0) {
            await setFieldValue('testChannels', [EMPTY_TEST_CHANNEL]);
            await setFieldError('testChannels', []);
        }
    }, [values.testChannels.length, setFieldValue]);

    return (
        <Form>
            <div className={css.headerContainer}>
                <div className={css.pageTitle}>
                    <Translate id={title} />
                </div>
                {shouldShowDraft && (
                    <div onClick={() => onSaveDraftClick()} className={css.saveDraftTitle}>
                        <Translate id={`${prefixTranslate}.BUTTONS.SAVE_DRAFT`} />
                    </div>
                )}
            </div>
            <div className={css.warningContainer}>
                <WarningMessage
                    type="error"
                    showIcon={false}
                    show={!!submitData?.error}
                    message={`${prefixTranslate}.SAVE_ERROR`}
                />
            </div>
            <div className={css.contentLayout}>
                <GeneralPopup
                    open={showPopup}
                    type={PopupTypes.INFO_WITH_CANCEL}
                    title={`${prefixTranslate}.SAVE_DRAFT_TITLE`}
                    rejectText="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.CONTINUE_EDIT"
                    acceptText="STATIC.BUTTONS.SAVE"
                    text={`${prefixTranslate}.SAVE_DRAFT_MESSAGE`}
                    onAccept={handlePopupAccept}
                    onReject={handlePopupCancel}
                    loading={submitData.loading}
                />
                <div className={`${css.leftContainer} ${isContentScrollable ? css.contentScrollable : ''}`}>
                    {children}
                </div>
                <div className={css.rightContainer}>{buttons}</div>
            </div>
        </Form>
    );
};

WizardLayout.propTypes = {
    title: PropTypes.string,
    children: PropTypes.element,
    buttons: PropTypes.element,
    onSaveDraft: PropTypes.func,
    shouldShowDraft: PropTypes.bool,
    isContentScrollable: PropTypes.bool,
    submitData: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.bool,
        message: PropTypes.string,
    }),
    activeKey: PropTypes.string,
};

export default WizardLayout;
